/**
 * Remote variant resolution for truncated PIV2 lists (variants missing from
 * embedded JSON). Keep in sync with test-harness/pdp/piv2-variant-fetch.mjs
 */
(function (global) {
  'use strict';

  const REMOTE_RESOLVE_PATH = '/api/resolve-variant-for-selection';
  // Single-flight promise cache keyed by product + comboKey: concurrent callers
  // (the picker's own render-time resolve and an explicit
  // ensureRemoteVariantResolved call) share the same request instead of the
  // second caller getting null.
  const resolvePromises = new Map();

  function storefrontPriceToCents(money) {
    if (!money?.amount) return null;
    const parsed = Number.parseFloat(String(money.amount));
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
  }

  function deriveResolveEndpointBase(endpoint) {
    const raw = String(endpoint || '').trim();
    if (!raw) return '';
    return raw.replace(/\/api\/encoded-availability\/?$/i, '');
  }

  function buildSelectedOptions(selection, options) {
    if (!Array.isArray(selection) || !Array.isArray(options)) return [];
    return selection
      .map((valueIndex, optionIndex) => {
        const name = options[optionIndex]?.name;
        const value = options[optionIndex]?.optionValues?.[valueIndex]?.name;
        if (!name || value == null) return null;
        return { name, value };
      })
      .filter(Boolean);
  }

  function needsRemoteVariantResolve(ctx) {
    if (typeof ctx.isSelectionComplete === 'function' && !ctx.isSelectionComplete(ctx.selection)) {
      return false;
    }

    const localVariant =
      typeof ctx.resolveVariant === 'function' ? ctx.resolveVariant() : null;
    if (localVariant) return false;

    if (!ctx.existCombos?.has?.(ctx.comboKey)) return false;

    const ctaState =
      typeof ctx.resolveCtaState === 'function' ? ctx.resolveCtaState() : ctx.ctaState;
    if (ctaState !== 'add' && ctaState !== 'notify') return false;

    return Boolean(ctx.variantsTruncated);
  }

  function normalizeRemoteVariant(body, selectedOptions) {
    if (!body?.variantId) return null;

    const price = storefrontPriceToCents(body.price);
    const compareAtPrice = storefrontPriceToCents(body.compareAtPrice);

    return {
      id: body.variantId,
      options: selectedOptions.map((entry) => entry.value),
      available: Boolean(body.availableForSale),
      price,
      compareAtPrice,
      source: body.source || 'storefront',
      recommended: body.recommended || null,
    };
  }

  /**
   * `/variants/<id>.js` returns `featured_image` as an object, but older
   * payloads hand back a bare URL string. Normalize to the `{ src }` shape
   * bundle-picker's getDirectVariantImageUrl reads.
   * @param {unknown} image
   * @returns {{ src: string } | null}
   */
  function toImageSrcObject(image) {
    if (!image) return null;
    if (typeof image === 'string') return { src: image };
    const src = image.src || image.url;
    return src ? { src } : null;
  }

  async function fetchVariantJson(variantId) {
    const response = await fetch(`/variants/${variantId}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) return null;
    return response.json();
  }

  async function enrichVariantFromShopify(variant) {
    if (!variant?.id) return variant;
    try {
      const shopifyVariant = await fetchVariantJson(variant.id);
      if (!shopifyVariant) return variant;
      return {
        ...variant,
        price: shopifyVariant.price ?? variant.price,
        compareAtPrice: shopifyVariant.compare_at_price ?? variant.compareAtPrice,
        priceFormatted: shopifyVariant.price_formatted || variant.priceFormatted,
        compareAtPriceFormatted:
          shopifyVariant.compare_at_price_formatted || variant.compareAtPriceFormatted,
        // Prefer the refetched image so a variant change cannot leave a stale
        // shot on the bundle review card; keep the SSR/sibling one otherwise.
        // Unlike the Storefront API (see piv2-sibling-product variantOwnImageUrl),
        // /variants/<id>.js returns featured_image null when the variant owns
        // no image — no featured-image fallback to strip here.
        featured_image: toImageSrcObject(shopifyVariant.featured_image) || variant.featured_image || null,
        quantity_rule: {
          min: shopifyVariant.quantity_rule?.min ?? 1,
          increment: shopifyVariant.quantity_rule?.increment ?? 1,
          max: shopifyVariant.quantity_rule?.max ?? null,
        },
      };
    } catch {
      return variant;
    }
  }

  async function requestVariantForSelection(ctx, comboKey) {
    const endpointBase = deriveResolveEndpointBase(ctx.endpoint);
    if (!endpointBase) return null;

    const selectedOptions = buildSelectedOptions(ctx.selection, ctx.options);
    if (!selectedOptions.length) return null;

    const url = new URL(`${endpointBase}${REMOTE_RESOLVE_PATH}`, window.location.origin);
    url.searchParams.set('handle', ctx.handle || '');
    url.searchParams.set('productId', String(ctx.productId || ''));
    url.searchParams.set('selectedOptions', JSON.stringify(selectedOptions));

    if (ctx.availCombos?.has?.(comboKey)) url.searchParams.set('expectAvailable', '1');

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) return null;

      const body = await response.json();
      let variant = normalizeRemoteVariant(body, selectedOptions);
      if (variant) {
        variant = await enrichVariantFromShopify(variant);
      }
      return variant;
    } catch {
      return null;
    }
  }

  async function fetchVariantForSelection(ctx) {
    const comboKey = ctx.comboKey;
    if (!comboKey) return null;

    // comboKey alone is only selection indices ("0:2:1") — on bundle pages two
    // pickers for different products share this module, so the cache key must
    // be product-scoped or one picker gets the other's variant.
    const cacheKey = `${ctx.productId || ctx.handle || ''}::${comboKey}`;

    const existing = resolvePromises.get(cacheKey);
    if (existing) return existing;

    const promise = requestVariantForSelection(ctx, comboKey);
    resolvePromises.set(cacheKey, promise);

    const variant = await promise;
    // A resolved variant stays cached (the settled promise is the cache); a
    // null/failed attempt is evicted so a later attempt for the same combo
    // (new selection round-trip or explicit submit) can retry.
    if (!variant && resolvePromises.get(cacheKey) === promise) {
      resolvePromises.delete(cacheKey);
    }
    return variant;
  }

  function clearResolveCache() {
    resolvePromises.clear();
  }

  global.Piv2VariantFetch = {
    REMOTE_RESOLVE_PATH,
    deriveResolveEndpointBase,
    buildSelectedOptions,
    needsRemoteVariantResolve,
    normalizeRemoteVariant,
    fetchVariantForSelection,
    clearResolveCache,
    storefrontPriceToCents,
  };
})(typeof globalThis !== 'undefined' ? globalThis : window);
